import { useCallback, useEffect, useState } from 'react';
import { refusalMessage } from '../api';

/**
 * One page of old orders, and the next on asking (InGo ADR 0034). `load` is
 * the realm's own (`pastOrdersApi.client` or `.rider`). Nothing old is not a
 * state to show: a client or rider who had none sees no section at all.
 */
export function usePastOrders(load) {
  const [orders, setOrders] = useState([]);
  const [next, setNext] = useState(null);
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    load().then(
      (page) => {
        if (cancelled) return;
        setOrders(page.orders);
        setNext(page.next);
      },
      (err) => !cancelled && setError(refusalMessage(err)),
    );

    return () => {
      cancelled = true;
    };
  }, [load]);

  const showOlder = useCallback(async () => {
    setMore(true);
    setError('');
    try {
      const page = await load(next);
      setOrders((shown) => [...shown, ...page.orders]);
      setNext(page.next);
    } catch (err) {
      setError(refusalMessage(err));
    } finally {
      setMore(false);
    }
  }, [load, next]);

  return { orders, next, error, more, showOlder };
}

/** "$12.30", in the currency the old system recorded. */
export function pastFare(order) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: order.currency || 'USD' }).format((Number(order.fare_minor) || 0) / 100);
}

/**
 * The day it ended (delivered or cancelled), else the day it was booked, as
 * the operator's calendar has it: the server writes each moment in the
 * operator's zone, so the day is its date part, whatever the phone's zone.
 */
export function pastDay(order) {
  const at = String(order.delivered_at || order.cancelled_at || order.booked_at || '');
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(at);

  return day
    ? new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]))).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '';
}
